import { useState, useCallback } from 'react';
import { DataTable, StatusField, Pagination, EmptyState, useSpinner, StatusType } from '@chikara/ui';
import { UserModal } from '@/features/team/components/UserModals';
import type { UserToEdit, TeamTableProps, UserDetails } from '@/features/team/types/team.types';
import { TeamStatus } from '@/features/team/types/team.types';
import { TEAM_COLUMNS, TEAM_MOBILE_CONFIG } from '@/features/team/constants/team.constants';
import { formatTableData } from '@/features/team/utils/team.utils';
import type { AccountDetail } from '@/features/card/types/account.types';
import styles from './TeamTable.module.css';
import dataNotFound from '../../assets/data-not-found.svg';
import { t } from '@/i18n';


export const TeamTable = ({ 
  members, 
  pagination,
  onApprove, 
  onUpdate, 
  onDeactivate,
  onGetMember
}: TeamTableProps) => {
  const { isSpinnerLoading } = useSpinner();
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [userToApprove, setUserToApprove] = useState<UserDetails | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<UserToEdit | null>(null);

  // GET
  const handleApproveClick = useCallback(async (item: AccountDetail) => {
    const data = await onGetMember(item.user_uuid as string);
    if (data) {
      data.account_uuid = item.uuid;
      setUserToApprove(data);
      setApproveModalOpen(true);
    }
  }, [onGetMember]);

  const handleEditClick = useCallback(async (item: AccountDetail) => {
    const data = await onGetMember(item.user_uuid as string);
    if (data) {
      setUserToEdit(data);
      setEditModalOpen(true);
    }
  }, [onGetMember]);


  // POST
  const handleApproveSubmit = useCallback(async (values: any) => {
    await onApprove(values.uuid, values, TeamStatus.APPROVED);
    setApproveModalOpen(false);
    setUserToApprove(null);
  }, [onApprove]);

  const handleRejectClick = useCallback(async (member_uuid: string) => {
    await onApprove(member_uuid, { status: TeamStatus.REJECTED }, TeamStatus.REJECTED);
    setApproveModalOpen(false);
    setUserToApprove(null);
  }, [onApprove]);

  const handleEditSave = useCallback(async (values: any) => {
    await onUpdate(values.uuid, values);
    setEditModalOpen(false);
    setUserToEdit(null);
  }, [onUpdate]);

  const handleDeactivateClick = useCallback(async (member_uuid: string) => {
    await onDeactivate(member_uuid);
    setEditModalOpen(false);
    setUserToEdit(null);
  }, [onDeactivate]);

  const tableData = formatTableData(members);

  return (
    <div className={styles.container}>
      { (members.length > 0 || isSpinnerLoading) ? <><DataTable
        columns={TEAM_COLUMNS}
        data={tableData}
        mobileConfig={TEAM_MOBILE_CONFIG}
        isLoading={isSpinnerLoading}
        renderCell={(key, value, row) => {
          const member = row._member as AccountDetail;
          if (key === 'status') {
            const status = value as string;
            const statusType = row._member.status as StatusType;
            const isPending = status === 'pending_approval';
            return (
              <StatusField
                status={status}
                statusType={statusType}
                statusColor={isPending ? 'error' : undefined}
              />
            );
          }

          if (key === 'action') {
            const isApprove = value === 'approve';
            const isView = value === 'view';

            return (
              isView ? <div>---</div> : <button 
                type="button"
                className={styles.actionLink}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isApprove) handleApproveClick(member);
                  else handleEditClick(member);
                }}
              >
                {isApprove ? t('team.actions.approve') : t('team.actions.edit')}
              </button>
            );
          }

          return value ?? '-';
        }}
      />
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={(page) => pagination.onPageChange(page, pagination.itemsPerPage)}
          showInfo
          infoText={t('dashboard.paginationInfo', {
            start: (pagination.currentPage - 1) * pagination.itemsPerPage + 1,
            end: Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems),
            total: pagination.totalItems
          })}
        />
      </>: <EmptyState
        title={t('team.emptyState.title')}
        description={t('team.emptyState.description')}
        imgSrc={dataNotFound}
      />}

      <UserModal
        mode="approve"
        isOpen={approveModalOpen}
        onClose={() => {
          setApproveModalOpen(false);
          setUserToApprove(null);
        }}
        user={userToApprove}
        onSubmit={handleApproveSubmit}
        onReject={handleRejectClick}
      />

      <UserModal
        mode="edit"
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setUserToEdit(null);
        }}
        user={userToEdit}
        onSubmit={handleEditSave}
        onDeactivate={handleDeactivateClick}
      />
    </div>
  );
};
